import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Play, Trash2, ChevronRight } from 'lucide-react';
import {
  api,
  type AiSummaryConfig,
  type AiSummaryRun,
  type KommoPipeline,
  type KommoStatus,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface KommoPipelineWithStatuses {
  pipeline: KommoPipeline;
  statuses: KommoStatus[];
}

export function AiSummaryDetail(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: cfg } = useQuery({
    queryKey: ['ai-summary-config', id],
    queryFn: () => api.get<AiSummaryConfig>(`/admin/ai-summary/configs/${id}`),
  });

  // Подтягиваем все воронки + статусы из Kommo для UI
  const { data: pipelines = [] } = useQuery({
    queryKey: ['kommo-pipelines-for-config', cfg?.kommo_account_id],
    queryFn: () =>
      api.get<KommoPipeline[]>(`/admin/accounts/${cfg!.kommo_account_id}/kommo-pipelines`),
    enabled: Boolean(cfg?.kommo_account_id),
  });

  const [draft, setDraft] = useState<{
    display_name: string;
    pipeline_ids: number[];
    status_filter: Record<string, number[]> | null;
    activity_days_window: number;
  } | null>(null);

  // (Пере)инициализируем draft при СМЕНЕ конфига (по cfg.id), а не только при
  // draft===null. Иначе при навигации config→config (компонент переиспользуется,
  // route без key) draft остался бы от предыдущего конфига и PATCH ушёл бы на новый
  // id со старыми значениями. Зависимость на id (не на cfg) не сбрасывает draft при
  // фоновом рефетче того же конфига.
  useEffect(() => {
    if (cfg) {
      setDraft({
        display_name: cfg.display_name,
        pipeline_ids: cfg.pipeline_ids,
        status_filter: cfg.status_filter,
        activity_days_window: cfg.activity_days_window,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.id]);

  const saveMutation = useMutation({
    mutationFn: (payload: NonNullable<typeof draft>) =>
      api.patch<{ ok: boolean }>(`/admin/ai-summary/configs/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-summary-config', id] });
      queryClient.invalidateQueries({ queryKey: ['ai-summary-configs'] });
    },
  });

  const runNowMutation = useMutation({
    mutationFn: () => api.post<{ ok: boolean; job_id: string }>(
      `/admin/ai-summary/configs/${id}/run-now`,
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-summary-runs', id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/admin/ai-summary/configs/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-summary-configs'] });
      navigate('/ai-summary');
    },
  });

  const { data: runs = [] } = useQuery({
    queryKey: ['ai-summary-runs', id],
    queryFn: () => api.get<AiSummaryRun[]>(`/admin/ai-summary/configs/${id}/runs`),
    refetchInterval: 5_000,
  });

  if (!cfg || !draft) {
    return (
      <div className="text-sm text-muted-foreground">Загрузка…</div>
    );
  }

  const togglePipeline = (pid: number): void => {
    setDraft((d) =>
      d
        ? {
            ...d,
            pipeline_ids: d.pipeline_ids.includes(pid)
              ? d.pipeline_ids.filter((x) => x !== pid)
              : [...d.pipeline_ids, pid],
          }
        : d,
    );
  };

  const toggleStatus = (pid: number, sid: number): void => {
    setDraft((d) => {
      if (!d) return d;
      const filter = d.status_filter ? { ...d.status_filter } : {};
      const current = filter[String(pid)] ?? [];
      const next = current.includes(sid)
        ? current.filter((x) => x !== sid)
        : [...current, sid];
      if (next.length === 0) {
        delete filter[String(pid)];
        return { ...d, status_filter: Object.keys(filter).length > 0 ? filter : null };
      }
      filter[String(pid)] = next;
      return { ...d, status_filter: filter };
    });
  };

  const selectedPipelines = pipelines.filter((p) => draft.pipeline_ids.includes(p.id));

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon">
            <Link to="/ai-summary"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{cfg.display_name}</h1>
            <p className="text-sm text-muted-foreground">
              {cfg.account_display_name} · {cfg.account_subdomain}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={cfg.status === 'active' ? 'success' : 'secondary'}>
            {cfg.status === 'active' ? 'Active' : 'Paused'}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            disabled={runNowMutation.isPending}
            onClick={() => runNowMutation.mutate()}
          >
            <Play className="h-3.5 w-3.5" /> Run now
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (window.confirm('Удалить конфиг?')) deleteMutation.mutate();
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Настройки</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Название</Label>
              <Input
                id="name"
                value={draft.display_name}
                onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="window">Окно активности (дней)</Label>
              <Input
                id="window"
                type="number"
                min="1"
                max="60"
                value={draft.activity_days_window}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    activity_days_window: Number(e.target.value) || 7,
                  })
                }
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Воронки Kommo</Label>
            {pipelines.length === 0 ? (
              <div className="text-xs text-muted-foreground">Загрузка списка…</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {pipelines.map((p) => {
                  const checked = draft.pipeline_ids.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => togglePipeline(p.id)}
                      className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                        checked
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:bg-accent'
                      }`}
                    >
                      {p.name}
                      {p.is_main ? ' ★' : ''}
                    </button>
                  );
                })}
                <p className="w-full text-xs text-muted-foreground">
                  {draft.pipeline_ids.length === 0
                    ? 'Не выбрана ни одна — раздел не будет запускаться.'
                    : `Выбрано: ${draft.pipeline_ids.length}`}
                </p>
              </div>
            )}
          </div>

          {selectedPipelines.map((p) => (
            <StatusPicker
              key={p.id}
              accountId={cfg.kommo_account_id}
              pipeline={p}
              selected={draft.status_filter?.[String(p.id)] ?? null}
              onToggle={(sid) => toggleStatus(p.id, sid)}
            />
          ))}

          <div className="flex justify-end">
            <Button
              onClick={() => saveMutation.mutate(draft)}
              disabled={saveMutation.isPending}
            >
              <Save className="h-4 w-4" />
              {saveMutation.isPending ? 'Сохраняем…' : 'Сохранить'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Последние запуски</CardTitle>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Запусков пока не было. Нажмите «Run now» сверху или дождитесь Пн 06:00 UTC.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead className="w-20">Trigger</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Done</TableHead>
                  <TableHead className="text-right">Skip</TableHead>
                  <TableHead className="text-right">Fail</TableHead>
                  <TableHead className="text-right">Tokens in</TableHead>
                  <TableHead className="text-right">Tokens out</TableHead>
                  <TableHead className="w-12 text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(r.started_at).toLocaleString()}
                      {!r.finished_at && (
                        <Badge variant="warning" className="ml-2">running</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.trigger === 'cron' ? 'secondary' : 'success'}>
                        {r.trigger}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.leads_total}</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-600">
                      {r.leads_processed}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {r.leads_skipped}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-destructive">
                      {r.leads_failed}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {r.llm_tokens_input.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {r.llm_tokens_output.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="ghost" size="icon" title="Детали">
                        <Link to={`/ai-summary/${id}/runs/${r.id}`}>
                          <ChevronRight className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatusPicker({
  accountId,
  pipeline,
  selected,
  onToggle,
}: {
  accountId: string;
  pipeline: KommoPipeline;
  selected: number[] | null;
  onToggle: (sid: number) => void;
}): JSX.Element {
  const { data: statuses = [] } = useQuery({
    queryKey: ['kommo-statuses', accountId, pipeline.id],
    queryFn: () =>
      api.get<KommoStatus[]>(
        `/admin/accounts/${accountId}/kommo-statuses?pipeline_id=${pipeline.id}`,
      ),
  });
  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <div className="flex items-center justify-between text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <span>{pipeline.name}</span>
        <span>
          {selected === null
            ? 'все статусы'
            : selected.length === 0
              ? 'не выбрано — пропустим'
              : `${selected.length} статусов`}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {statuses.map((s) => {
          const isOn = selected === null ? true : selected.includes(s.id);
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onToggle(s.id)}
              className={`rounded-md border px-2 py-1 text-xs transition-colors ${
                isOn
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:bg-accent'
              }`}
            >
              {s.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
